// src/actions/commerce_admin_actions.js
// Admin Orders backed by the Saleor-compatible commerce edge (POST /commerce/graphql).
// Uses the same conventions as the other action files: plain axios + the jwtToken
// bearer from localStorage + classic dispatch thunks. Action-type constants are kept
// local to this module to avoid touching the shared types.js.
import axios from "axios";

export const CA_ORDERS_LOADING = "CA_ORDERS_LOADING";
export const CA_ORDERS_SUCCESS = "CA_ORDERS_SUCCESS";
export const CA_ORDERS_FAIL = "CA_ORDERS_FAIL";
export const CA_ORDER_LOADING = "CA_ORDER_LOADING";
export const CA_ORDER_SUCCESS = "CA_ORDER_SUCCESS";
export const CA_ORDER_FAIL = "CA_ORDER_FAIL";
export const CA_ACTION_LOADING = "CA_ACTION_LOADING";
export const CA_ACTION_SUCCESS = "CA_ACTION_SUCCESS";
export const CA_ACTION_FAIL = "CA_ACTION_FAIL";

// Single entry point to the commerce GraphQL endpoint. Sends the dashboard's
// staff login token so the server resolves ctx.staff for admin operations.
async function commerceGraphql(query, variables) {
  const base = process.env.REACT_APP_ADMIN_API_ADDRESS || "";
  const token = localStorage.getItem("jwtToken");
  const resp = await axios.post(
    `${base}/commerce/graphql`,
    { query, variables },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const body = resp.data || {};
  if (body.errors && body.errors.length) {
    throw new Error(body.errors[0].message || "GraphQL error");
  }
  return body.data;
}

const ORDER_FIELDS = `
  id number created userEmail status paymentStatusDisplay source
  channel { slug }
  total { gross { amount currency } }
`;

const ORDERS_QUERY = `
  query($first: Int, $after: String, $filter: OrderFilterInput) {
    orders(first: $first, after: $after, filter: $filter) {
      totalCount
      pageInfo { hasNextPage endCursor }
      edges { node { ${ORDER_FIELDS} } }
    }
  }`;

const ORDER_QUERY = `
  query($id: ID!) {
    order(id: $id) {
      ${ORDER_FIELDS}
      subtotal { gross { amount currency } }
      shippingPrice { gross { amount currency } }
      total { gross { amount currency } tax { amount currency } }
      totalRefunded { amount currency }
      discounts { amount { amount currency } }
      trackingClientId
      shippingAddress { firstName lastName streetAddress1 streetAddress2 city countryArea postalCode country { code country } phone }
      billingAddress { firstName lastName streetAddress1 city countryArea postalCode country { code } }
      deliveryMethod { __typename ... on ShippingMethod { name } ... on Warehouse { email } }
      fulfillments { id status trackingNumber }
      lines {
        productName variantName productSku quantity
        thumbnail(format: ORIGINAL, size: 64) { url }
        unitPrice { gross { amount currency } }
        totalPrice { gross { amount currency } }
      }
    }
  }`;

// Fetch a page of orders. filter = { status } (OrderStatusFilter enum) or null.
export const fetchCommerceOrders = ({ first = 25, after = null, status = null, includeTest = false } = {}) => async (dispatch) => {
  dispatch({ type: CA_ORDERS_LOADING });
  try {
    // The engine hides is_test orders from this list by DEFAULT, so /orders/manage
    // shows what a human would call real orders. includeTest is the deliberate
    // opt-in: without a way to switch it on, a rehearsal order would be
    // completely invisible and impossible to inspect, which is worse than showing
    // it — the whole point of tagging rather than deleting is that it stays
    // findable.
    const filter = (status || includeTest) ? { ...(status ? { status } : {}), ...(includeTest ? { includeTest: true } : {}) } : null;
    const data = await commerceGraphql(ORDERS_QUERY, { first, after, filter });
    dispatch({ type: CA_ORDERS_SUCCESS, payload: { connection: data.orders, append: !!after } });
  } catch (e) {
    dispatch({ type: CA_ORDERS_FAIL, payload: e.message });
  }
};

export const fetchCommerceOrder = (id) => async (dispatch) => {
  dispatch({ type: CA_ORDER_LOADING });
  try {
    const data = await commerceGraphql(ORDER_QUERY, { id });
    dispatch({ type: CA_ORDER_SUCCESS, payload: data.order });
  } catch (e) {
    dispatch({ type: CA_ORDER_FAIL, payload: e.message });
  }
};

// ---- admin mutations (staff-only server-side). Each refetches the order after. ----

const M_FULFILL = `mutation($order: ID!) { orderFulfill(order: $order) { errors { code message } } }`;
const M_TRACK = `mutation($id: ID!, $in: TrackingInput!) { orderFulfillmentUpdateTracking(id: $id, input: $in) { errors { code message } } }`;
const M_CANCEL = `mutation($id: ID!) { orderCancel(id: $id) { errors { code message } } }`;
const M_REFUND = `mutation($id: ID!) { orderMarkAsRefunded(id: $id) { errors { code message } } }`;
const M_PARTIAL_REFUND = `mutation($id: ID!, $amount: PositiveDecimal!) { orderMarkPartiallyRefunded(id: $id, amount: $amount) { errors { code message } } }`;

function payloadErrors(data) {
  const p = data && (data.orderFulfill || data.orderFulfillmentUpdateTracking || data.orderCancel || data.orderMarkAsRefunded || data.orderMarkPartiallyRefunded);
  return p && p.errors && p.errors.length ? p.errors : null;
}

// Runs a mutation, surfaces a payload-level error if present, then refetches the order.
const runAction = (query, variables, orderId) => async (dispatch) => {
  dispatch({ type: CA_ACTION_LOADING });
  try {
    const data = await commerceGraphql(query, variables);
    const errs = payloadErrors(data);
    if (errs) {
      dispatch({ type: CA_ACTION_FAIL, payload: `${errs[0].code}: ${errs[0].message}` });
      return;
    }
    dispatch({ type: CA_ACTION_SUCCESS });
    dispatch(fetchCommerceOrder(orderId));
    return { ok: true };
  } catch (e) {
    dispatch({ type: CA_ACTION_FAIL, payload: e.message });
    return { ok: false, error: e.message };
  }
};

export const fulfillOrder = (orderId) => runAction(M_FULFILL, { order: orderId }, orderId);

// Fulfil AND attach tracking as ONE operator action.
//
// WHY THIS EXISTS: orderFulfill alone sends no shipping email and does NOT release
// the PayPal payment hold. Both of those live in orderFulfillmentUpdateTracking
// (engine: commerce/graphql/orderRepo.js). With two separate buttons, an operator who
// clicked Fulfill and stopped had shipped the goods while the buyer's funds stayed
// held and the seller went unpaid — and nothing said so. The main Orders page already
// requires tracking before it will submit a fulfilment; this brings the admin page
// into line with it.
//
// The fulfilment id is not known until the fulfil returns, which is why this cannot be
// composed in the component: it needs the mutation's own response.
const M_FULFILL_RETURNING = `mutation($order: ID!) {
  orderFulfill(order: $order) {
    order { fulfillments { id trackingNumber } }
    errors { code message }
  }
}`;

export const fulfillAndTrack = (orderId, trackingNumber, carrier) => async (dispatch) => {
  dispatch({ type: CA_ACTION_LOADING });
  try {
    const data = await commerceGraphql(M_FULFILL_RETURNING, { order: orderId });
    const errs = payloadErrors(data);
    if (errs) {
      // Nothing was fulfilled — this includes NOT_ENGINE_OWNED on a mirrored order,
      // whose message already names the Saleor record to use instead.
      const msg = `${errs[0].code}: ${errs[0].message}`;
      dispatch({ type: CA_ACTION_FAIL, payload: msg });
      return { ok: false, error: msg };
    }

    const fulfillments =
      (data.orderFulfill && data.orderFulfill.order && data.orderFulfill.order.fulfillments) || [];
    // Prefer a fulfilment with no tracking yet; otherwise the newest one.
    const target =
      fulfillments.find((f) => !f.trackingNumber) || fulfillments[fulfillments.length - 1];

    if (!target) {
      // Fulfilled, but we cannot attach tracking, so the hold is still held. Say that
      // plainly rather than reporting success.
      const msg =
        "Order was marked fulfilled, but no fulfilment came back so tracking could not be attached — the customer was not emailed and the PayPal hold is NOT released. Use Add Tracking.";
      dispatch({ type: CA_ACTION_FAIL, payload: msg });
      dispatch(fetchCommerceOrder(orderId));
      return { ok: false, error: msg, fulfilled: true };
    }

    // Carrier forwarded. Without it the engine's TrackingInput carried only a
    // tracking number, PayPal's paypalCarrier() fell through to 'OTHER', and the
    // buyer got a tracking link that resolves to nothing.
    const t = await commerceGraphql(M_TRACK, {
      id: target.id,
      in: carrier ? { trackingNumber, carrier } : { trackingNumber },
    });
    const tErrs = payloadErrors(t);
    if (tErrs) {
      // PARTIAL SUCCESS, and it must not read as total failure: the goods ARE recorded
      // as fulfilled. Only the step that emails the customer and releases the hold
      // failed, so the operator must know to retry that step specifically.
      const msg = `Fulfilled, but tracking failed (${tErrs[0].code}: ${tErrs[0].message}) — the customer was not emailed and the PayPal hold is NOT released. Retry with Add Tracking.`;
      dispatch({ type: CA_ACTION_FAIL, payload: msg });
      dispatch(fetchCommerceOrder(orderId));
      return { ok: false, error: msg, fulfilled: true };
    }

    dispatch({ type: CA_ACTION_SUCCESS });
    dispatch(fetchCommerceOrder(orderId));
  } catch (e) {
    dispatch({ type: CA_ACTION_FAIL, payload: e.message });
  }
};
export const cancelOrder = (orderId) => runAction(M_CANCEL, { id: orderId }, orderId);
export const markOrderRefunded = (orderId) => runAction(M_REFUND, { id: orderId }, orderId);
export const markOrderPartiallyRefunded = (orderId, amount) => runAction(M_PARTIAL_REFUND, { id: orderId, amount }, orderId);
export const addTracking = (fulfillmentId, trackingNumber, orderId, carrier) =>
  runAction(M_TRACK, { id: fulfillmentId, in: carrier ? { trackingNumber, carrier } : { trackingNumber } }, orderId);

// ---- P5d: admin management (products / pages / discounts) ----
export const CA_ADMIN_LOADING = "CA_ADMIN_LOADING";
export const CA_ADMIN_PRODUCTS = "CA_ADMIN_PRODUCTS";
export const CA_ADMIN_PAGES = "CA_ADMIN_PAGES";
export const CA_ADMIN_VOUCHERS = "CA_ADMIN_VOUCHERS";
export const CA_ADMIN_FAIL = "CA_ADMIN_FAIL";
export const CA_ADMIN_SAVING = "CA_ADMIN_SAVING";
export const CA_ADMIN_SAVED = "CA_ADMIN_SAVED";
export const CA_ADMIN_SAVE_FAIL = "CA_ADMIN_SAVE_FAIL";

const load = (type, run) => async (dispatch) => {
  dispatch({ type: CA_ADMIN_LOADING });
  try { dispatch({ type, payload: await run() }); }
  catch (e) { dispatch({ type: CA_ADMIN_FAIL, payload: e.message }); }
};

// One shape for the list and the detail page, so a product looks the same on both.
//
// `channels` (not `prices`) drives the availability matrix: prices carries one
// entry per LISTING, so a channel the variant is not sold in is simply absent and
// cannot be told apart from one priced at zero. channels returns a row per channel
// that exists, with a null amount when unlisted.
//
// REQUIRES ENGINE v41. Against v40 this whole query fails with "Cannot query field
// channels", which would take the existing Products page down with it — so this UI
// must not deploy before the commerce build ships.
const ADMIN_PRODUCT_FIELDS = `
  id slug name description isPublished productType
  metadata{ key value }
  media{ id url alt type sortOrder }
  variants{
    id sku name mediaIds
    stock{ quantity allocated available }
    prices{ channel amount currency }
    channels{ channel name currency amount available }
  }`;

export const fetchAdminProducts = () => load(CA_ADMIN_PRODUCTS, async () =>
  (await commerceGraphql(`query{ adminProducts(first:200){ items{ ${ADMIN_PRODUCT_FIELDS} } } }`)).adminProducts.items);

// ---- catalog CMS detail page ----
export const CA_PRODUCT_LOADING = "CA_PRODUCT_LOADING";
export const CA_PRODUCT_SUCCESS = "CA_PRODUCT_SUCCESS";
export const CA_PRODUCT_FAIL = "CA_PRODUCT_FAIL";
export const CA_PRODUCT_SAVING = "CA_PRODUCT_SAVING";
export const CA_PRODUCT_SAVED = "CA_PRODUCT_SAVED";

// The engine exposes no adminProduct(id) query — only the adminProducts list — so
// the detail page reads the list and selects. Fine at six products; if the catalogue
// ever passes ~50, add a single-product query rather than growing this payload.
//
// It still dispatches to its OWN state keys rather than reusing adminProducts: the
// list and the detail page load independently, and sharing one key lets a slow list
// response overwrite a freshly-saved detail.
export const fetchAdminProduct = (productId) => async (dispatch) => {
  dispatch({ type: CA_PRODUCT_LOADING });
  try {
    const data = await commerceGraphql(`query{ adminProducts(first:200){ items{ ${ADMIN_PRODUCT_FIELDS} } } }`);
    const found = (data.adminProducts.items || []).find((p) => String(p.id) === String(productId));
    if (!found) throw new Error(`Product ${productId} not found`);
    dispatch({ type: CA_PRODUCT_SUCCESS, payload: found });
  } catch (e) {
    dispatch({ type: CA_PRODUCT_FAIL, payload: e.message });
  }
};

const M_VARIANT_CHANNEL = `mutation($in:AdminVariantChannelInput!){ adminVariantChannelSet(input:$in){ ok errors{ code message } } }`;
const M_VARIANT_UPSERT = `mutation($in:AdminVariantInput!){ adminVariantUpsert(input:$in){ ok id errors{ code message } } }`;
const M_MEDIA_REORDER = `mutation($in:AdminMediaReorderInput!){ adminProductMediaReorder(input:$in){ ok errors{ code message } } }`;
const M_MEDIA_DELETE = `mutation($id:Int!){ adminProductMediaDelete(mediaId:$id){ ok errors{ code message } } }`;
const M_VARIANT_MEDIA = `mutation($in:AdminVariantMediaInput!){ adminVariantMediaSet(input:$in){ ok errors{ code message } } }`;

// Run one mutation and normalise BOTH failure shapes into the same result:
// a thrown transport/GraphQL error, and a 200 carrying { ok:false, errors:[...] }.
async function runOne(key, query, variables, resultKey) {
  try {
    const data = await commerceGraphql(query, variables);
    const r = data[resultKey];
    if (r && r.ok === false) {
      const e = (r.errors && r.errors[0]) || {};
      return { key, ok: false, code: e.code || "ERROR", message: e.message || "save failed" };
    }
    return { key, ok: true, id: r && r.id };
  } catch (e) {
    return { key, ok: false, code: "TRANSPORT", message: e.message };
  }
}

// Save the whole product form.
//
// The engine has no bulk mutation, so one Save fans out to N calls. Results are
// reported PER FIELD: a partial failure must never surface as a single "save
// failed" over a write that half-succeeded — the operator has to know which cells
// to fix, and the caller keeps those dirty.
//
// PRICES RUN BEFORE AVAILABILITY, and that ordering is load-bearing.
// adminVariantChannelSet refuses with NO_PRICE when the channel has no listing,
// because turning availability on would otherwise have to invent a price of 0 and
// publish the variant as free. Setting a price is what creates the listing. Ticking
// "available" and entering a price in the same save is the ordinary case for a
// newly-listed channel, so running them concurrently would lose that race roughly
// half the time.
export const saveProductForm = (productId, changes) => async (dispatch) => {
  dispatch({ type: CA_PRODUCT_SAVING });
  const c = changes || {};
  const results = [];

  // phase 1 — product fields and prices (prices create listings)
  const phase1 = [];
  if (c.product) {
    phase1.push(runOne("product", `mutation($in:AdminProductInput!){ adminProductUpsert(input:$in){ ok id errors{ code message } } }`,
      { in: { id: Number(productId), ...c.product } }, "adminProductUpsert"));
  }
  for (const v of c.variants || []) {
    phase1.push(runOne(`variant:${v.variantId || "new"}`, M_VARIANT_UPSERT,
      { in: { id: v.variantId, productId: Number(productId), sku: v.sku, name: v.name } }, "adminVariantUpsert"));
  }
  for (const p of c.prices || []) {
    phase1.push(runOne(`price:${p.variantId}:${p.channel}`, `mutation($in:AdminVariantPriceInput!){ adminVariantPriceSet(input:$in){ ok errors{ code message } } }`,
      { in: { variantId: p.variantId, channel: p.channel, amount: p.amount } }, "adminVariantPriceSet"));
  }
  for (const s of c.stock || []) {
    phase1.push(runOne(`stock:${s.variantId}`, `mutation($in:AdminStockInput!){ adminStockSet(input:$in){ ok errors{ code message } } }`,
      { in: { variantId: s.variantId, quantity: s.quantity } }, "adminStockSet"));
  }
  results.push(...(await Promise.all(phase1)));

  // phase 2 — availability, now that any new listing exists
  const phase2 = (c.availability || []).map((a) =>
    runOne(`available:${a.variantId}:${a.channel}`, M_VARIANT_CHANNEL,
      { in: { variantId: a.variantId, channel: a.channel, available: a.available } }, "adminVariantChannelSet"));
  results.push(...(await Promise.all(phase2)));

  const failures = results.filter((r) => !r.ok);
  dispatch({ type: CA_PRODUCT_SAVED, payload: { results, failures } });
  dispatch(fetchAdminProduct(productId));
  return { ok: failures.length === 0, failures, results };
};

// Media manager. Each refreshes the product because sort_order is recomputed
// server-side (delete closes the gap), so local optimistic order would drift.
export const reorderProductMedia = (productId, mediaIds) => async (dispatch) => {
  dispatch({ type: CA_ADMIN_SAVING });
  const r = await runOne("reorder", M_MEDIA_REORDER, { in: { productId: Number(productId), mediaIds } }, "adminProductMediaReorder");
  dispatch(r.ok ? { type: CA_ADMIN_SAVED } : { type: CA_ADMIN_SAVE_FAIL, payload: `${r.code}: ${r.message}` });
  dispatch(fetchAdminProduct(productId));
  return r;
};

export const deleteProductMedia = (productId, mediaId) => async (dispatch) => {
  dispatch({ type: CA_ADMIN_SAVING });
  const r = await runOne("delete", M_MEDIA_DELETE, { id: Number(mediaId) }, "adminProductMediaDelete");
  dispatch(r.ok ? { type: CA_ADMIN_SAVED } : { type: CA_ADMIN_SAVE_FAIL, payload: `${r.code}: ${r.message}` });
  dispatch(fetchAdminProduct(productId));
  return r;
};

// Per-variant image selection. mediaIds order IS the display order; an empty list
// clears the selection and falls the variant back to the whole product pool.
export const setVariantMedia = (productId, variantId, mediaIds) => async (dispatch) => {
  dispatch({ type: CA_ADMIN_SAVING });
  const r = await runOne("variantMedia", M_VARIANT_MEDIA, { in: { variantId: Number(variantId), mediaIds } }, "adminVariantMediaSet");
  dispatch(r.ok ? { type: CA_ADMIN_SAVED } : { type: CA_ADMIN_SAVE_FAIL, payload: `${r.code}: ${r.message}` });
  dispatch(fetchAdminProduct(productId));
  return r;
};

// Multi-file image/video upload (multipart) → appends to the product's media.
export const uploadProductMedia = (productId, files) => async (dispatch) => {
  dispatch({ type: CA_ADMIN_SAVING });
  try {
    const base = process.env.REACT_APP_ADMIN_API_ADDRESS || "";
    const token = localStorage.getItem("jwtToken");
    const fd = new FormData();
    fd.append("productId", String(productId));
    for (const f of files) fd.append("files", f);
    const resp = await fetch(`${base}/commerce/media/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (!resp.ok) {
      const b = await resp.json().catch(() => ({}));
      throw new Error((b.errors && b.errors[0] && b.errors[0].message) || `upload failed (${resp.status})`);
    }
    dispatch({ type: CA_ADMIN_SAVED });
    dispatch(fetchAdminProducts());
  } catch (e) { dispatch({ type: CA_ADMIN_SAVE_FAIL, payload: e.message }); }
};
export const fetchAdminPages = () => load(CA_ADMIN_PAGES, async () =>
  (await commerceGraphql(`query{ adminPages{ id slug title content pageType metadata{ key value } } }`)).adminPages);
export const fetchAdminVouchers = () => load(CA_ADMIN_VOUCHERS, async () =>
  (await commerceGraphql(`query{ adminVouchers{ code kind value channelScope active } }`)).adminVouchers);

// Generic save: run a mutation, surface {ok,errors}, then refresh via `after`.
const save = (query, variables, resultKey, after) => async (dispatch) => {
  dispatch({ type: CA_ADMIN_SAVING });
  try {
    const data = await commerceGraphql(query, variables);
    const r = data[resultKey];
    if (r && r.ok === false) { dispatch({ type: CA_ADMIN_SAVE_FAIL, payload: (r.errors[0] && `${r.errors[0].code}: ${r.errors[0].message}`) || "save failed" }); return; }
    dispatch({ type: CA_ADMIN_SAVED });
    if (after) dispatch(after());
  } catch (e) { dispatch({ type: CA_ADMIN_SAVE_FAIL, payload: e.message }); }
};

export const saveAdminProduct = (input) => save(`mutation($in:AdminProductInput!){ adminProductUpsert(input:$in){ ok errors{ code message } } }`, { in: input }, "adminProductUpsert", fetchAdminProducts);
export const setAdminPrice = (input) => save(`mutation($in:AdminVariantPriceInput!){ adminVariantPriceSet(input:$in){ ok errors{ code message } } }`, { in: input }, "adminVariantPriceSet", fetchAdminProducts);
export const setAdminStock = (input) => save(`mutation($in:AdminStockInput!){ adminStockSet(input:$in){ ok errors{ code message } } }`, { in: input }, "adminStockSet", fetchAdminProducts);
export const setProductMedia = (productId, urls) => save(`mutation($in:AdminMediaInput!){ adminProductMediaSet(input:$in){ ok errors{ code message } } }`, { in: { productId, urls } }, "adminProductMediaSet", fetchAdminProducts);
export const saveAdminPage = (input) => save(`mutation($in:AdminPageInput!){ adminPageUpsert(input:$in){ ok errors{ code message } } }`, { in: input }, "adminPageUpsert", fetchAdminPages);
export const saveAdminVoucher = (input) => save(`mutation($in:AdminVoucherInput!){ adminVoucherUpsert(input:$in){ ok errors{ code message } } }`, { in: input }, "adminVoucherUpsert", fetchAdminVouchers);
export const setVoucherActive = (code, active) => save(`mutation($c:String!,$a:Boolean!){ adminVoucherSetActive(code:$c,active:$a){ ok errors{ code message } } }`, { c: code, a: active }, "adminVoucherSetActive", fetchAdminVouchers);

export { commerceGraphql, ORDER_QUERY };
